import { HttpClient } from '@angular/common/http';
import {
  Injectable,
  inject,
  signal,
  makeStateKey,
  TransferState,
  PLATFORM_ID,
  effect,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  AuthUser,
  createAbility,
} from '@bossanova/models';
import { PureAbility } from '@casl/ability';
import { Observable, tap, catchError, of, map, shareReplay } from 'rxjs';
import { APP_CONFIG, INITIAL_AUTH_USER } from '../app.config.token';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const USER_KEY = makeStateKey<AuthUser | null>('auth_user');

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);
  private config = inject(APP_CONFIG);
  private apiUrl = `${this.config.apiUrl}/auth`;

  isAuthenticated = signal<boolean>(false);
  currentUser = signal<AuthUser | null>(null);

  // Cache the observable to share it among subscribers
  private authCheck$: Observable<boolean> | null = null;
  private initialUser = inject(INITIAL_AUTH_USER, { optional: true });
  private ability = inject(PureAbility);

  constructor() {
    this.checkAuth().pipe(takeUntilDestroyed()).subscribe();

    effect(() => {
      const user = this.currentUser();
      // Update ability rules based on current user
      const { rules } = createAbility(
        user ? { id: user.userId, role: user.role } : null,
      );
      this.ability.update(rules);
    });
  }

  checkAuth(): Observable<boolean> {
    // If we already have a pending or completed check, return it
    if (this.authCheck$) {
      return this.authCheck$;
    }

    if (this.initialUser) {
      this.isAuthenticated.set(true);
      this.currentUser.set(this.initialUser);

      // Save to TransferState if running on server so client hydration picks it up
      if (isPlatformServer(this.platformId)) {
        this.transferState.set(USER_KEY, this.initialUser);
      }

      this.authCheck$ = of(true).pipe(shareReplay(1));
      return this.authCheck$;
    }

    // Server-side optimization: If initialUser was not found above, we are unauthenticated.
    // Avoid making a loopback HTTP request that is guaranteed to fail (missing/invalid cookie).
    if (isPlatformServer(this.platformId)) {
      this.transferState.set(USER_KEY, null);
      return of(false);
    }

    // Check if we have data in TransferState (from SSR)

    if (this.transferState.hasKey(USER_KEY)) {
      const user = this.transferState.get(USER_KEY, null);
      this.transferState.remove(USER_KEY);

      if (user) {
        this.isAuthenticated.set(true);
        this.currentUser.set(user);

        this.authCheck$ = of(true).pipe(shareReplay(1));
        return this.authCheck$;
      }

      // Server explicitly checked and found no user — trust that result
      this.authCheck$ = of(false).pipe(shareReplay(1));
      return this.authCheck$;
    }

    this.authCheck$ = this.http
      .get<AuthUser>(`${this.apiUrl}/profile`, {
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
      .pipe(
        tap((user) => {
          this.isAuthenticated.set(true);
          this.currentUser.set(user);

          // Save to TransferState if running on server
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(USER_KEY, user);
          }
        }),
        map(() => true),
        catchError(() => {
          // Clear auth state on any error (including 401)
          this.isAuthenticated.set(false);
          this.currentUser.set(null);
          return of(false);
        }),
        shareReplay(1), // Share the result with all subscribers
        takeUntilDestroyed(),
      );

    return this.authCheck$;
  }

  login(data: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/login`, data).pipe(
      tap((response) => {
        this.setSession(response);
      }),
    );
  }

  register(data: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${this.apiUrl}/register`, data);
  }

  confirmEmail(code: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/confirm`, {
        code,
      })
      .pipe(
        tap((response) => {
          this.setSession(response);
        }),
      );
  }

  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/forgot-password`, { email });
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/reset-password`, {
      token,
      newPassword,
    });
  }

  logout(): void {
    // Clear local state immediately (optimistic logout)
    this.clearSession();
    // Fire and forget the server logout
    this.http.post(`${this.apiUrl}/logout`, {}).subscribe();
  }

  clearSession(): void {
    this.isAuthenticated.set(false);
    this.currentUser.set(null);
    this.authCheck$ = null;
    this.transferState.remove(USER_KEY);
  }

  updateAvatar(file: File): Observable<AuthUser> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post<AuthUser>(`${this.config.apiUrl}/me/avatar`, formData)
      .pipe(
        tap((user) => {
          // Update local state
          const currentUser = this.currentUser();
          if (currentUser) {
            this.currentUser.set({
              ...currentUser,
              avatarPath: user.avatarPath,
            });
          }
        }),
      );
  }

  removeAvatar(): Observable<void> {
    return this.http.delete<void>(`${this.config.apiUrl}/me/avatar`).pipe(
      tap(() => {
        const currentUser = this.currentUser();
        if (currentUser) {
          this.currentUser.set({ ...currentUser, avatarPath: undefined });
        }
      }),
    );
  }

  updateDescription(
    description: string,
  ): Observable<{ description: string | null }> {
    return this.http
      .patch<{
        description: string | null;
      }>(`${this.config.apiUrl}/me/description`, { description })
      .pipe(
        tap((result) => {
          const currentUser = this.currentUser();
          if (currentUser) {
            this.currentUser.set({
              ...currentUser,
              description: result.description,
            });
          }
        }),
      );
  }

  private setSession(authResult: LoginResponse | RegisterResponse): void {
    const user: AuthUser = {
      userId: authResult.id,
      username: authResult.username,
      avatarPath:
        'avatarPath' in authResult ? authResult.avatarPath : undefined,
      role: authResult.role,
    };

    this.isAuthenticated.set(true);
    this.currentUser.set(user);
    this.authCheck$ = of(true).pipe(shareReplay(1));
  }
}
